import React, { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { HubConnectionBuilder, HubConnection, LogLevel } from '@microsoft/signalr';
import { toast } from 'react-toastify';
import { Creators } from '../store/reducer';
import { useHistory } from 'react-router-dom';
import { State } from '../store';

const PaymentListener: React.FC = () => {
    const dispatch = useDispatch();
    const history = useHistory();
    const { tenantId, logado, token } = useSelector((state: State) => state);
    const connectionRef = useRef<HubConnection | null>(null);
    const logoutFiredRef = useRef(false);
    const sessionIdRef = useRef(0);

    useEffect(() => {
        if (!logado || !tenantId) {
             if (connectionRef.current) {
                connectionRef.current.stop();
                connectionRef.current = null;
            }
            return;
        }

        // Cada nova sessão (login) gera um novo ID para invalidar timeouts de sessões anteriores
        const currentSession = ++sessionIdRef.current;
        logoutFiredRef.current = false;

        // O hub exige o JWT (só a própria empresa entra no seu grupo); SignalR envia em ?access_token=.
        const newConnection = new HubConnectionBuilder()
            .withUrl(`${process.env.REACT_APP_API_URL}/paymentHub`, { accessTokenFactory: () => token || '' })
            .withAutomaticReconnect()
            .configureLogging(LogLevel.Information)
            .build();

        connectionRef.current = newConnection;

        // Depois de uma reconexão a conexão é NOVA no servidor e não pertence mais ao grupo da empresa:
        // sem refazer o JoinGroup o aviso "Pagamento confirmado" nunca chegava (queda de rede durante o PIX).
        newConnection.onreconnected(async () => {
            try { await newConnection.invoke('JoinGroup', tenantId); }
            catch (err) { console.error('SignalR JoinGroup após reconexão: ', err); }
        });

        const startConnection = async () => {
            try {
                await newConnection.start();

                await newConnection.invoke('JoinGroup', tenantId);

                newConnection.on('PaymentConfirmed', (message: string) => {
                    // Se o checkout está ativo, ele mesmo gerencia o fluxo de logout
                    const activePayment = localStorage.getItem('activePayment');
                    if (activePayment) {
                        return;
                    }

                    // Previne múltiplos logouts
                    if (logoutFiredRef.current) return;

                    toast.success(message, {
                        autoClose: 5000,
                    });

                    // Único logout com delay para o usuário ler a mensagem
                    setTimeout(() => {
                        // Verifica se a sessão ainda é a mesma (evita matar sessão nova após re-login)
                        if (logoutFiredRef.current || sessionIdRef.current !== currentSession) return;
                        logoutFiredRef.current = true;
                        dispatch(Creators.logOut());
                        history.push('/?toast=success');
                    }, 3000);
                });

            } catch (err) {
                console.error('SignalR Connection Error: ', err);
            }
        };

        startConnection();

        return () => {
            if (connectionRef.current) {
                connectionRef.current.off('PaymentConfirmed');
                connectionRef.current.stop();
            }
        };
    }, [logado, tenantId, token, dispatch, history]);

    return null;
};

export default PaymentListener;
